import React, { useEffect, useState } from 'react';
import {
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue
} from 'react-aria-components';
import type { ListBoxItemProps } from 'react-aria-components';
import { Check, ChevronExpand } from 'react-bootstrap-icons';
import styles from './solution_status_drodown.module.css';

interface IStatusItem {
  ident: string;
  title: string;
  color: string;
}

interface ISolutionStatusDropDownProps {
  statusArray: IStatusItem[];
  selectedStatus: string;
  onStatusChange?: (statusIdent: string) => void;
  isDisabled?: boolean;
}

export const SolutionStatusDropDown: React.FC<ISolutionStatusDropDownProps> = ({
  statusArray,
  selectedStatus,
  onStatusChange,
  isDisabled = false
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string>('');

  useEffect(() => {
    setSelectedItemId(selectedStatus);
  }, [selectedStatus]);

  return (
    <Select
      className={`d-flex position-relative ${styles.selectRoot}`}
      style={{ width: 220 }}
      selectedKey={selectedItemId}
      isDisabled={isDisabled}
      onSelectionChange={key => {
        if (key != null) {
          const newKey = String(key);
          setSelectedItemId(newKey);
          onStatusChange?.(newKey);
        }
      }}
      placeholder="All states"
    >
      <Button
        className={
          'btn btn-light d-flex align-items-center justify-content-between w-100 text-start ' +
          styles.dropDownButton
        }
      >
        <SelectValue className="flex-grow-1 text-truncate" />
        <ChevronExpand className={`ms-2 ${styles.dropDownIcon}`} size={16} />
      </Button>

      <Popover
        className={"dropdown-menu show mt-1 w-100 " + styles.list_style}
        style={{ maxHeight: 240, maxWidth: 220, overflowY: 'auto' }}
      >
        <ListBox className={`list-unstyled mb-0 ${styles.listBoxRoot}`} items={statusArray}>
          {(item: IStatusItem) => (
            <StatusItem
              id={item.ident}
              key={item.ident}
              textValue={item.title || item.ident}
            >
              <Status color={item.color} />
              {item.title || item.ident}
            </StatusItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
};

function StatusItem(
  props: ListBoxItemProps & { children: React.ReactNode }
) {
  return (
    <ListBoxItem {...props} className={styles.listItemRoot}>
      {({ isSelected, isFocused, isHovered }) => {
        const rowClass = [
          'd-flex align-items-center justify-content-between',
          styles.dropDownItemRow,
          isSelected && styles.dropDownItemRowSelected,
          isFocused && styles.dropDownItemRowFocused,
          !isFocused && isHovered && styles.dropDownItemRowHovered
        ]
          .filter(Boolean)
          .join(' ');

        const textClass = [
          'd-flex align-items-center gap-2 text-truncate',
          isFocused ? styles.dropDownTextFocused : '',
          isSelected && !isFocused ? styles.dropDownTextSelected : ''
        ]
          .filter(Boolean)
          .join(' ');

        const iconClass = [
          'd-flex align-items-center justify-content-center ms-2',
          isFocused ? styles.checkIconFocused : styles.checkIconDefault
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div className={rowClass}>
            <span
              className={`${textClass} ${styles.dropDownText}`}
            >
              {props.children}
            </span>
            <span className={iconClass} style={{ width: 20 }}>
              {isSelected && <Check size={16} />}
            </span>
          </div>
        );
      }}
    </ListBoxItem>
  );
}

function Status({ color }: { color: string }) {
  return (
    <span
      className={`rounded-circle ${styles.statusDot}`}
      style={{
        width: 10,
        height: 10,
        display: 'inline-block',
        backgroundColor: color || 'transparent'
      }}
    />
  );
}
